/**
 * TreeMinimap — extracted from FamilyTreeDetail (Sep 2026 refactor kickoff).
 *
 * A scaled-down sketch of the whole family tree with:
 *   - the current viewport outlined in copper,
 *   - a red "you are here" box around the selected member's card.
 *
 * Click or drag anywhere on the minimap to jump the main canvas to that
 * location. Kept purely presentational — all layout math still lives in
 * the parent component and is passed in via props, which is why this
 * file has zero data-fetching, zero context usage, and no imports beyond
 * React. That keeps future extractions of the tree canvas trivial.
 */
import React from "react";

export default function TreeMinimap({
  layout,
  tx, ty, scale,
  canvasBox,
  onNavigate,
  highlightId = null,
  cardHeight,   // TREE_CARD_H — parent passes so the minimap doesn't have to import it
}) {
  if (!layout.width || !layout.height || !canvasBox) return null;

  const k = Math.min(150 / layout.width, 110 / layout.height, 1);
  const w = Math.max(56, layout.width * k);
  const h = Math.max(40, layout.height * k);

  const navigate = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    onNavigate(
      ((e.clientX - box.left) / box.width) * layout.width,
      ((e.clientY - box.top) / box.height) * layout.height,
    );
  };

  // Find the highlighted member's rectangle so we can draw the red "you
  // are here" marker. Graceful fallback to null when the id doesn't
  // match anything (e.g. it's an orphan or a stale selection).
  let highlight = null;
  if (highlightId && layout.unitById) {
    for (const u of layout.unitById.values()) {
      if (u.x == null || u.y == null) continue;
      const members = [u.a, u.b].filter(Boolean);
      if (members.some((m) => m && m.id === highlightId)) {
        highlight = { x: u.x, y: u.y, w: u.w, h: cardHeight };
        break;
      }
    }
  }

  return (
    <div
      className="absolute bottom-3 right-3 z-20 rounded-lg border shadow-md overflow-hidden"
      style={{
        background: "rgba(255,253,247,0.92)",
        borderColor: "rgba(160,124,60,0.5)",
        backdropFilter: "blur(4px)",
      }}
      data-testid="tree-minimap"
      title="Minimap — click or drag to move around the tree"
    >
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        preserveAspectRatio="none"
        className="block cursor-pointer"
        onPointerDown={(e) => {
          e.stopPropagation();
          navigate(e);
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
        }}
        onPointerMove={(e) => { if (e.buttons) { e.stopPropagation(); navigate(e); } }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {Array.from(layout.unitById.values()).map((u) => (
          u.x == null || u.y == null || layout.orphanUnitIds.has(u.id) ? null : (
            <rect
              key={u.id}
              x={u.x} y={u.y}
              width={u.w} height={cardHeight}
              rx={18}
              fill="#B58936"
              opacity={0.55}
            />
          )
        ))}
        <rect
          x={-tx / scale} y={-ty / scale}
          width={canvasBox.width / scale} height={canvasBox.height / scale}
          fill="rgba(178,93,56,0.12)"
          stroke="#B25D38"
          strokeWidth={Math.max(3, layout.width / 90)}
          data-testid="tree-minimap-viewport"
        />
        {highlight && (
          <rect
            x={highlight.x - 8} y={highlight.y - 8}
            width={highlight.w + 16} height={highlight.h + 16}
            rx={22}
            fill="rgba(220,38,38,0.18)"
            stroke="#DC2626"
            strokeWidth={Math.max(4, layout.width / 70)}
            data-testid="tree-minimap-highlight"
          />
        )}
      </svg>
    </div>
  );
}
